import { Database, ListChecks, TrendingUp, Users, BarChart3 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import { DashboardContainer } from '@/features/studio-dashboard/components/dashboard-container'
import { OperationsTabs } from '@/features/studio-dashboard/components/operations-tabs'
import { PageHeader } from '@/components/page-header'
import { pushStudioRoute } from '@/lib/studio-navigation'
import { studioRouteLabel } from '@/lib/studio-route-registry'
import { useStudioLocale } from '@/i18n/use-studio-locale'

/** Shown where a number would be if anything measured it. */
const UNMEASURED = '—'

interface OverviewTile {
  route: 'products' | 'orders' | 'revenue' | 'members' | 'analytics'
  icon: LucideIcon
}

// Icon only. The tile's NAME comes from the route registry through
// `studioRouteLabel`, so the tile, the tab strip above it, and the breadcrumb
// cannot drift apart; its description comes from the dictionary.
const TILES: OverviewTile[] = [
  { route: 'products', icon: Database },
  { route: 'orders', icon: ListChecks },
  { route: 'revenue', icon: TrendingUp },
  { route: 'members', icon: Users },
  { route: 'analytics', icon: BarChart3 },
]

export function CommerceOverviewPage() {
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)
  const { strings } = useStudioLocale()
  const opsCopy = strings.operations

  const handleTileClick = (route: OverviewTile['route']) => {
    setStudioRoute(route)
    pushStudioRoute(route)
  }

  return (
    <div className="h-full overflow-y-auto bg-background">
      <DashboardContainer className="py-6 space-y-6 max-w-7xl">
        <OperationsTabs activeRoute="commerce" />
        <div className="space-y-6">
            <PageHeader
              route="commerce"
              title={opsCopy.title}
              description={opsCopy.subtitle}
            />

            {/*
              These three tiles used to read 48 / 68% / 9 with "12.4% from last
              month" and "5.2% baseline positive" under them — five JSX literals
              on a page that imports no data source at all. Nothing measures
              them, so nothing is claimed: the values read "—" until a source
              exists.
            */}
            <section className="grid grid-cols-1 gap-4 rounded-xl border border-border bg-card p-5 shadow-sm sm:grid-cols-3" aria-label={strings.a11y.reviewSummary} data-testid="review-summary">
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground uppercase font-medium">{opsCopy.reviewedOutputs}</span>
                <div className="text-2xl font-bold text-foreground">{UNMEASURED}</div>
              </div>
              <div className="space-y-1 border-t sm:border-t-0 sm:border-l border-border/40 pt-4 sm:pt-0 sm:pl-5">
                <span className="text-xs text-muted-foreground uppercase font-medium">{opsCopy.acceptanceRate}</span>
                <div className="text-2xl font-bold text-foreground">{UNMEASURED}</div>
              </div>
              <div className="space-y-1 border-t sm:border-t-0 sm:border-l border-border/40 pt-4 sm:pt-0 sm:pl-5">
                <span className="text-xs text-muted-foreground uppercase font-medium">{opsCopy.sharedArtifacts}</span>
                <div className="text-2xl font-bold text-foreground">{UNMEASURED}</div>
                <span className="text-[10px] text-muted-foreground">
                  {opsCopy.sharedArtifactsNote}
                </span>
              </div>
              <p className="text-xs text-muted-foreground sm:col-span-3" data-testid="review-summary-unmeasured">
                {opsCopy.unmeasured}
              </p>
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {TILES.map((tile) => {
                const Icon = tile.icon
                return (
                  <button
                    key={tile.route}
                    type="button"
                    onClick={() => handleTileClick(tile.route)}
                    className="group flex flex-col items-start gap-4 rounded-xl border border-border/60 bg-card p-5 text-left transition-all hover:border-foreground/20 hover:bg-muted/30 hover:shadow-sm"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-muted-foreground group-hover:text-foreground transition-all">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm font-semibold text-foreground transition-colors">
                        {studioRouteLabel(tile.route, strings)}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-2">
                        {opsCopy.tiles[tile.route]}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
        </div>
      </DashboardContainer>
    </div>
  )
}
